import { Alert, Box, Button, CircularProgress, Collapse, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { com } from '@eidu/web'
import { useSortedEntityTypes } from '../../io/useEntityTypes'
import { usePermissionPolicy } from '../../io/usePermissionPolicies'
import {
  useCreatePermissionPolicy,
  useDeletePermissionPolicy,
  useModifyPermissionPolicy,
} from '../../io/usePermissionPolicyMutations'
import { requireNotUndefinedOrNull } from '../../util/require'
import PermissionError from '../entity/PermissionError'
import { PermissionWithId } from '../../components/input/PermissionsInput'
import ConfirmationDialog from '../../components/layout/ConfirmationDialog'
import PermissionPolicyForm from '../../components/layout/PermissionPolicyForm'
import { useSnackBar } from '../../components/feedback/SnackBarProvider'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import permissionPolicyIdFromString = com.eidu.sharedlib.entity.permission.permissionPolicyIdFromString
import useHasGlobalPermissions from './useHasGlobalPermissions'
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction

type CreateOrEditPermissionPolicyPageContentProps = {
  entityTypes: readonly EntityType[]
  name: string
  setName: (name: string) => void
  selectedPermissions: readonly PermissionWithId[]
  setSelectedPermissions: (permissions: readonly PermissionWithId[]) => void
  onSubmit: () => Promise<void>
  onDelete?: () => Promise<void>
  onCancel: () => void
  isEditMode: boolean
  canSave: boolean
}

const CreateOrEditPermissionPolicyPageContent = ({
  entityTypes,
  name,
  setName,
  selectedPermissions,
  setSelectedPermissions,
  onSubmit,
  onDelete,
  onCancel,
  isEditMode,
  canSave,
}: CreateOrEditPermissionPolicyPageContentProps) => (
  <Stack padding={3}>
    <Stack direction="row" spacing={1} justifyContent="space-between">
      <Typography variant="h4">{isEditMode ? 'Update policy' : 'New policy'}</Typography>
      {isEditMode && onDelete && (
        <ConfirmationDialog
          title="Are you sure you want to delete this policy? This action cannot be undone."
          content={<Typography>Delete</Typography>}
          onConfirm={onDelete}
        />
      )}
    </Stack>
    <Box sx={{ paddingY: 2 }}>
      <PermissionPolicyForm
        entityTypes={entityTypes}
        name={name}
        setName={setName}
        selectedPermissions={selectedPermissions}
        setSelectedPermissions={setSelectedPermissions}
      />
    </Box>
    <Stack direction="row" spacing={2} sx={{ padding: 2, borderRadius: 1, justifyContent: 'flex-end' }}>
      <Button color="primary" variant="contained" onClick={onCancel}>
        Cancel
      </Button>
      <Button color="primary" variant="contained" onClick={onSubmit} disabled={!canSave}>
        Save
      </Button>
    </Stack>
  </Stack>
)

const CreateOrEditPermissionPolicyPageRoute = ({ isEditMode }: { isEditMode: boolean }) => {
  const navigate = useNavigate()
  const { showSnackBar } = useSnackBar()
  const params = useParams()
  const entityTypes = useSortedEntityTypes() ?? []

  const policyId = isEditMode ? permissionPolicyIdFromString(requireNotUndefinedOrNull(params.policyId)) : undefined
  const policyQuery = usePermissionPolicy(policyId)
  const createMutation = useCreatePermissionPolicy()
  const modifyMutation = useModifyPermissionPolicy()
  const deleteMutation = useDeletePermissionPolicy()

  const [name, setName] = useState<string>('')
  const [selectedPermissions, setSelectedPermissions] = useState<readonly PermissionWithId[]>([])

  const { hasPermission } = useHasGlobalPermissions(GlobalAction.ManagePermissionGrants)
  const hasManagePermissionGrantsPermission = hasPermission(GlobalAction.ManagePermissionGrants)

  useEffect(() => {
    if (isEditMode && policyQuery.policy) {
      setName(policyQuery.policy.policy.name)
      setSelectedPermissions(
        [...policyQuery.policy.policy.permissions.asJsReadonlySetView()].map((permission) => ({
          id: crypto.randomUUID(),
          permission,
        }))
      )
    }
  }, [isEditMode, policyQuery.policy])

  const submit = useCallback(async () => {
    const trimmedName = name.trim()
    try {
      const permissions = selectedPermissions.map(({ permission }) => permission)
      if (isEditMode && policyId) {
        await modifyMutation.mutateAsync({ id: policyId, name: trimmedName, permissions })
        showSnackBar(`Policy updated`, 'success')
      } else {
        await createMutation.mutateAsync({ name: trimmedName, permissions })
        showSnackBar(`Policy created`, 'success')
      }
      navigate(-1)
    } catch (e) {
      console.error(e)
      showSnackBar(`Failed to ${isEditMode ? 'update' : 'create'} policy`, 'error')
    }
  }, [name, selectedPermissions, isEditMode, policyId, createMutation, modifyMutation])

  const onCancel = useCallback(() => navigate(-1), [navigate])

  const onDelete = useCallback(async () => {
    if (!isEditMode || !policyId) return
    try {
      await deleteMutation.mutateAsync(policyId)
      showSnackBar(`Deleted policy`, 'success')
      navigate(-1)
    } catch (e) {
      console.error(e)
      showSnackBar(`Failed to delete policy`, 'error')
    }
  }, [isEditMode, policyId, deleteMutation])

  if (entityTypes.length === 0) return null

  const submitting = createMutation.isPending || modifyMutation.isPending || deleteMutation.isPending
  const loading = policyQuery.isLoading || submitting
  const canSave = name.trim().length > 0 && selectedPermissions.length > 0 && !submitting
  const error = policyQuery.error

  if (policyQuery.isLoading) return <CircularProgress sx={{ margin: 4 }} />

  return (
    <Stack>
      <Collapse in={!!error}>
        <Alert variant="filled" severity="error" sx={{ margin: 2 }}>
          <Box>{String(error)}</Box>
          <Box>Please reload the page and try again.</Box>
        </Alert>
      </Collapse>
      {!error && hasManagePermissionGrantsPermission && (
        <CreateOrEditPermissionPolicyPageContent
          entityTypes={entityTypes}
          name={name}
          setName={setName}
          selectedPermissions={selectedPermissions}
          setSelectedPermissions={setSelectedPermissions}
          onSubmit={submit}
          onDelete={isEditMode ? onDelete : undefined}
          onCancel={onCancel}
          isEditMode={isEditMode}
          canSave={canSave}
        />
      )}
      {!loading && !hasManagePermissionGrantsPermission && (
        <PermissionError missingPermittedActions={[GlobalAction.ManagePermissionGrants]} />
      )}
    </Stack>
  )
}

export default CreateOrEditPermissionPolicyPageRoute
